import React, { useRef, useState } from "react";
import { Box, IconButton, InputAdornment, Popover, TextField, Typography } from "@mui/material";
import { CalendarMonth, ChevronLeft, ChevronRight } from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material/styles";

// Seletor de competência multiplataforma no lugar de <input type="month"> (sem suporte
// no Firefox desktop): campo somente-leitura que abre um popover com navegação de ano
// e grade de 12 meses — mesma experiência do picker nativo do Chrome, em todo navegador.
// Contrato de valor: "YYYY-MM" (o mesmo dos relatórios).
interface MonthSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Primeiro mês selecionável (inclusive), "YYYY-MM". Default: 2024-01. */
  minMonth?: string;
  /** Último mês selecionável (inclusive), "YYYY-MM". Default: mês corrente. */
  maxMonth?: string;
  sx?: SxProps<Theme>;
}

const MONTH_LABELS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const currentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const split = (ym: string): [number, number] => {
  const [y, m] = ym.split("-").map(Number);
  return [y, m];
};

const format = (ym: string): string => {
  if (!ym) {
    return "";
  }
  const [y, m] = split(ym);
  return `${String(m).padStart(2, "0")}/${y}`;
};

const MonthSelect: React.FC<MonthSelectProps> = ({
  label,
  value,
  onChange,
  minMonth = "2024-01",
  maxMonth,
  sx,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const max = maxMonth ?? currentMonth();
  const [selYear, selMonth] = value ? split(value) : split(currentMonth());
  const [viewYear, setViewYear] = useState(selYear);
  const [minYear, minMonthNum] = split(minMonth);
  const [maxYear, maxMonthNum] = split(max);

  const openPicker = () => {
    setViewYear(value ? split(value)[0] : split(currentMonth())[0]);
    setOpen(true);
  };

  const isDisabled = (month: number): boolean =>
    viewYear < minYear || viewYear > maxYear
    || (viewYear === minYear && month < minMonthNum)
    || (viewYear === maxYear && month > maxMonthNum);

  return (
    <>
      <TextField
        ref={anchorRef}
        size="small"
        label={label}
        value={format(value)}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            openPicker();
          }
        }}
        fullWidth
        sx={{ ...sx, "& .MuiInputBase-input": { cursor: "pointer", caretColor: "transparent" } }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, "aria-haspopup": "dialog" }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonth fontSize="small" sx={{ color: "#6c757d", pointerEvents: "none" }} />
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, width: 232 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <IconButton
              size="small"
              onClick={() => setViewYear((y) => y - 1)}
              disabled={viewYear <= minYear}
              aria-label="Ano anterior"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{viewYear}</Typography>
            <IconButton
              size="small"
              onClick={() => setViewYear((y) => y + 1)}
              disabled={viewYear >= maxYear}
              aria-label="Próximo ano"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.5 }}>
            {MONTH_LABELS.map((name, idx) => {
              const month = idx + 1;
              const selected = viewYear === selYear && month === selMonth && Boolean(value);
              return (
                <Box
                  key={name}
                  component="button"
                  type="button"
                  disabled={isDisabled(month)}
                  onClick={() => {
                    onChange(`${viewYear}-${String(month).padStart(2, "0")}`);
                    setOpen(false);
                  }}
                  sx={{
                    border: "none",
                    borderRadius: "6px",
                    py: 0.9,
                    fontSize: 13,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    backgroundColor: selected ? "#03B4C6" : "transparent",
                    color: selected ? "#fff" : "#212529",
                    fontWeight: selected ? 600 : 400,
                    "&:hover:not(:disabled)": {
                      backgroundColor: selected ? "#03B4C6" : "#e9ecef",
                    },
                    "&:disabled": { color: "#ced4da", cursor: "default" },
                  }}
                >
                  {name}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default MonthSelect;
